import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  Package, AlertTriangle, TrendingUp, TrendingDown,
  Download, Printer, Search, Filter, ShoppingCart,
  BarChart3, PieChart, Activity, Truck, Calendar
} from 'lucide-react';

import ReportLayout from '../components/ReportLayout';
import { Link } from 'react-router-dom';

const formatDate = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString('en-KE', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return value;
  }
};

const formatMonth = (value) => {
  if (!value) return '';
  try {
    const [year, month] = value.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(month) - 1]} ${year}`;
  } catch {
    return value;
  }
};

const currency = (v) => `KES ${Number(v || 0).toLocaleString()}`;

const escapeCsv = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const toCsv = (rows, headers) => {
  const headerLine = headers.map((h) => escapeCsv(h.label)).join(',');
  const dataLines = rows.map((r) => headers.map((h) => escapeCsv(h.value(r))).join(','));
  return `${headerLine}\n${dataLines}`;
};

const downloadCsv = (filename, csv) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Stock Movement Bar Chart
const StockMovementChart = ({ data = [], height = 260 }) => {
  const labels = data.map((d) => formatDate(d.date).split(',')[0] || d.date?.slice(5));
  const n = Math.max(1, labels.length);

  const stockIn = data.map((d) => Number(d.stock_in || 0));
  const stockOut = data.map((d) => Number(d.stock_out || 0));

  const maxV = Math.max(1, ...stockIn, ...stockOut);

  const barW = Math.max(8, 400 / n - 8);
  const chartWidth = Math.max(400, n * 40);

  return (
    <div className="w-full">
      <div className="flex items-center gap-5 text-xs text-gray-500 mb-3">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 bg-green-500 inline-block rounded-sm" />
          <span>Stock In</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 bg-red-500 inline-block rounded-sm" />
          <span>Stock Out</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${chartWidth} ${height}`} preserveAspectRatio="none" style={{ width: '100%', height: `${height}px` }}>
          <line x1={0} x2={chartWidth} y1={height - 40} y2={height - 40} stroke="#E5E7EB" strokeWidth="1" />
          {data.map((d, i) => {
            const x = (i * (barW * 2 + 8)) + 20;
            const inH = (stockIn[i] / maxV) * (height - 60);
            const outH = (stockOut[i] / maxV) * (height - 60);
            return (
              <g key={d.date || i}>
                <rect x={x} y={height - 40 - inH} width={barW} height={Math.max(inH, 2)} fill="#22C55E" rx="2" />
                <rect x={x + barW + 4} y={height - 40 - outH} width={barW} height={Math.max(outH, 2)} fill="#EF4444" rx="2" />
                <text x={x + barW} y={height - 10} textAnchor="middle" fontSize="9" fill="#9CA3AF">
                  {labels[i]}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

// Inventory Value Trend Line Chart
const InventoryValueTrendChart = ({ data = [], height = 220 }) => {
  const values = data.map((d) => Number(d.total_value || 0));
  const maxV = Math.max(1, ...values);
  const minV = Math.min(...values, 0);

  const n = Math.max(1, data.length);
  const chartWidth = Math.max(400, n * 50);
  const leftPad = 30;
  const rightPad = 10;
  const topPad = 20;
  const bottomPad = 30;
  const plotWidth = chartWidth - leftPad - rightPad;
  const plotHeight = height - topPad - bottomPad;

  const points = data.map((d, i) => {
            const x = leftPad + (i * plotWidth) / (n - 1 || 1);
            const y = topPad + ((maxV - d.total_value) / (maxV - minV || 1)) * plotHeight;
            return { x, y };
          });

  const pathD = points.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : ` L ${p.x} ${p.y}`)).join('');

  return (
    <div className="w-full">
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${chartWidth} ${height}`} preserveAspectRatio="none" style={{ width: '100%', height: `${height}px` }}>
          <line x1={leftPad} x2={chartWidth - rightPad} y1={topPad} y2={topPad} stroke="#F3F4F6" strokeWidth="1" strokeDasharray="4,4" />
          <line x1={leftPad} x2={chartWidth - rightPad} y1={height - bottomPad} y2={height - bottomPad} stroke="#F3F4F6" strokeWidth="1" strokeDasharray="4,4" />
          <path d={pathD} fill="none" stroke="#2563EB" strokeWidth="2" />
          {points.map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r="4" fill="#2563EB" />
          ))}
          {data.map((d, i) => {
            const x = leftPad + (i * plotWidth) / (n - 1 || 1);
            return (
              <text key={i} x={x} y={height - 10} textAnchor="middle" fontSize="9" fill="#9CA3AF">
                {formatMonth(d.month)}
              </text>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

// Donut Chart for Category Distribution
const DonutChart = ({ slices = [], size = 220 }) => {
  const total = slices.reduce((s, x) => s + Number(x.total_value || 0), 0) || 1;
  const colors = ['#2563EB', '#F97316', '#16A34A', '#7C3AED', '#06B6D4', '#9CA3AF', '#EC4899', '#FACC15'];
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  let acc = 0;

  const toAngle = (frac) => frac * 360 - 90;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const labelPos = (frac, midFrac) => {
    const midAngle = toAngle(frac - midFrac / 2);
    const rad = toRad(midAngle);
    const labelRadius = radius + 16;
    return {
      x: 60 + labelRadius * Math.cos(rad),
      y: 60 + labelRadius * Math.sin(rad),
    };
  };

  return (
    <div className="flex items-center justify-center">
      <svg width="240" height="200" viewBox="0 0 160 140">
        <circle cx="60" cy="70" r={radius} stroke="#E5E7EB" strokeWidth="10" fill="none" />
        {slices.map((s, i) => {
          const value = Number(s.total_value || 0);
          const pct = value / total;
          const dash = pct * circumference;
          const gap = circumference - dash;
          const strokeDasharray = `${dash} ${gap}`;
          const strokeDashoffset = -acc * circumference;
          const midFrac = pct;
          const pos = labelPos(acc + pct, midFrac);
          const labelPct = (pct * 100).toFixed(1);
          const showLabel = pct > 0.05;
          acc += pct;

          return (
            <g key={s.category || i}>
              <circle
                cx="60"
                cy="70"
                r={radius}
                fill="none"
                stroke={colors[i % colors.length]}
                strokeWidth="10"
                strokeDasharray={strokeDasharray}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
                transform="rotate(-90 60 70)"
              />
              {showLabel && (
                <>
                  <text
                    x={pos.x}
                    y={pos.y - 4}
                    textAnchor="middle"
                    fontSize="7"
                    fill={colors[i % colors.length]}
                    fontWeight="600"
                  >
                    {s.category}
                  </text>
                  <text
                    x={pos.x}
                    y={pos.y + 8}
                    textAnchor="middle"
                    fontSize="7"
                    fill="#6B7280"
                  >
                    {labelPct}%
                  </text>
                </>
              )}
            </g>
          );
        })}
        <text x="60" y="66" textAnchor="middle" dominantBaseline="central" fontSize="8" fill="#6B7280">
          Total
        </text>
        <text x="60" y="80" textAnchor="middle" fontSize="10" fontWeight="700" fill="#1F2937">
          {currency(total)}
        </text>
      </svg>
    </div>
  );
};

const ReportsInventory = () => {
  const getCurrentMonthRange = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const toISODate = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    return { start: toISODate(start), end: toISODate(end) };
  }, []);

  const [dateRange, setDateRange] = useState({ start: getCurrentMonthRange.start, end: getCurrentMonthRange.end });
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [errorOnce, setErrorOnce] = useState(false);

  const fetchInventoryReport = async () => {
    try {
      setLoading(true);
      const res = await api.get('/inventory-report/report', {
        params: { startDate: dateRange.start, endDate: dateRange.end },
      });
      setReportData(res.data);
      setErrorOnce(false);
    } catch (e) {
      if (!errorOnce) {
        toast.error('Failed to load inventory report');
        setErrorOnce(true);
      }
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventoryReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.start, dateRange.end]);

  const summary = reportData?.summary || {};
  const stockMovement = reportData?.stock_movement || [];
  const categoryDistribution = reportData?.category_distribution || [];
  const lowStockItems = reportData?.low_stock_items || [];
  const expiringProducts = reportData?.expiring_products || [];
  const supplierPerformance = reportData?.supplier_performance || [];
  const inventoryRegister = reportData?.inventory_register || [];
  const inventoryValueTrend = reportData?.inventory_value_trend || [];

  // KPI definitions
  const topKpis = [
    {
      label: 'Total Stock Items',
      value: (summary.total_items || 0).toLocaleString(),
      icon: <Package size={20} />,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-700',
    },
    {
      label: 'Inventory Value',
      value: currency(summary.inventory_value),
      icon: <ShoppingCart size={20} />,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-700',
    },
    {
      label: 'Low Stock Items',
      value: (summary.low_stock_count || 0).toLocaleString(),
      icon: <AlertTriangle size={20} />,
      iconBg: 'bg-red-100',
      iconColor: 'text-red-700',
    },
    {
      label: 'Expiring Products',
      value: (summary.expiring_count || 0).toLocaleString(),
      icon: <Calendar size={20} />,
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-700',
    },
    {
      label: 'Pending Purchase Orders',
      value: (summary.pending_orders || 0).toLocaleString(),
      icon: <Truck size={20} />,
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-700',
    },
  ];

  // Export CSV
  const exportCsv = () => {
    const headers = [
      { label: 'Product', value: (r) => r.name },
      { label: 'SKU', value: (r) => r.sku },
      { label: 'Category', value: (r) => r.category },
      { label: 'Stock', value: (r) => r.quantity_in_stock },
      { label: 'Min Stock', value: (r) => r.min_stock_level },
      { label: 'Expiry Date', value: (r) => r.expiry_date },
      { label: 'Selling Price', value: (r) => r.selling_price },
      { label: 'Purchase Price', value: (r) => r.purchase_price },
    ];
    const csv = toCsv(inventoryRegister, headers);
    downloadCsv(`inventory_report_${dateRange.start}_to_${dateRange.end}.csv`, csv);
  };

  // Left slot: charts
  const leftSlot = (
    <div className="space-y-6">
      {/* Stock Movement Chart */}
      <div className="rounded-xl bg-white border border-[#EEF2F7] p-6 shadow-[0_2px_8px_rgba(15,23,42,0.05)]">
        <h2 className="text-lg font-semibold text-[#1F2937] mb-4">Stock Movement (Last 30 Days)</h2>
        {loading ? (
          <div className="h-64 flex items-center justify-center text-gray-400 text-sm">Loading…</div>
        ) : stockMovement.length > 0 ? (
          <StockMovementChart data={stockMovement} />
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-400 text-sm">No stock movement data available.</div>
        )}
      </div>

      {/* Inventory Value Trend */}
      <div className="rounded-xl bg-white border border-[#EEF2F7] p-6 shadow-[0_2px_8px_rgba(15,23,42,0.05)]">
        <h2 className="text-lg font-semibold text-[#1F2937] mb-4">Inventory Value Trend</h2>
        {loading ? (
          <div className="h-56 flex items-center justify-center text-gray-400 text-sm">Loading…</div>
        ) : inventoryValueTrend.length > 0 ? (
          <InventoryValueTrendChart data={inventoryValueTrend} />
        ) : (
          <div className="h-56 flex items-center justify-center text-gray-400 text-sm">No trend data available.</div>
        )}
      </div>
    </div>
  );

  // Right slot: category distribution and supplier performance
  const rightSlot = (
    <div className="space-y-6">
      {/* Category Distribution Donut Chart */}
      <div className="rounded-xl bg-white border border-[#EEF2F7] p-6 shadow-[0_2px_8px_rgba(15,23,42,0.05)]">
        <h2 className="text-lg font-semibold text-[#1F2937] mb-4">Category Distribution</h2>
        {loading ? (
          <div className="h-64 flex items-center justify-center text-gray-400 text-sm">Loading…</div>
        ) : categoryDistribution.length > 0 ? (
          <div>
            <DonutChart slices={categoryDistribution} />
            <div className="mt-4 space-y-2">
              {categoryDistribution.map((item, idx) => (
                <div key={item.category} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 capitalize">{item.category}</span>
                  <span className="font-semibold text-gray-800">{currency(item.total_value)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-400 text-sm">No category data available.</div>
        )}
      </div>

      {/* Supplier Performance */}
      <div className="rounded-xl bg-white border border-[#EEF2F7] p-6 shadow-[0_2px_8px_rgba(15,23,42,0.05)]">
        <div className="flex items-center gap-2 mb-4">
          <Truck size={18} className="text-purple-600" />
          <h2 className="text-lg font-semibold text-[#1F2937]">Supplier Performance</h2>
        </div>
        {loading ? (
          <div className="h-48 flex items-center justify-center text-gray-400 text-sm">Loading…</div>
        ) : supplierPerformance.length > 0 ? (
<div className="overflow-x-auto">
            <table className="w-full report-table">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Supplier</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Orders</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {supplierPerformance.slice(0, 5).map((s, idx) => (
                  <tr key={s.id || idx} className="hover:bg-gray-50">
                    <td className="px-3 py-2.5 text-sm text-gray-800 max-w-[120px] truncate" title={s.company_name}>
                      {s.company_name}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-right text-gray-700">{s.total_orders}</td>
                    <td className="px-3 py-2.5 text-sm text-right font-semibold text-green-700">{currency(s.total_value_purchased)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="h-32 flex items-center justify-center text-gray-400 text-sm">No supplier data available.</div>
        )}
      </div>
    </div>
  );

  // Tables slot: low stock, expiring products, and complete register
  const tablesSlot = (
    <div className="space-y-6">
      {/* Low Stock Alert Table */}
      <div className="rounded-xl bg-white border border-[#EEF2F7] shadow-[0_2px_8px_rgba(15,23,42,0.05)] overflow-hidden">
        <div className="px-6 py-4 border-b border-[#EEF2F7]">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-600" />
            <h2 className="text-lg font-semibold text-[#1F2937]">Low Stock Alert</h2>
          </div>
          <p className="text-sm text-gray-500 mt-1">{lowStockItems.length} items need restocking</p>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600" />
          </div>
        ) : lowStockItems.length === 0 ? (
          <div className="px-6 py-16 text-center text-gray-500">No low stock items</div>
        ) : (
<div className="overflow-x-auto">
            <table className="w-full report-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Product</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">SKU</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Current</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Min</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Shortage</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lowStockItems.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{p.name}</td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-500">{p.sku}</td>
                    <td className="px-4 py-3 text-sm text-center font-bold text-red-700">{p.quantity_in_stock}</td>
                    <td className="px-4 py-3 text-sm text-center text-gray-700">{p.min_stock_level}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-red-600">{p.shortage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Expiring Products Table */}
      <div className="rounded-xl bg-white border border-[#EEF2F7] shadow-[0_2px_8px_rgba(15,23,42,0.05)] overflow-hidden">
        <div className="px-6 py-4 border-b border-[#EEF2F7]">
          <div className="flex items-center gap-2">
            <Calendar size={18} className="text-orange-600" />
            <h2 className="text-lg font-semibold text-[#1F2937]">Expiring Products</h2>
          </div>
          <p className="text-sm text-gray-500 mt-1">{expiringProducts.length} products expiring soon</p>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600" />
          </div>
        ) : expiringProducts.length === 0 ? (
          <div className="px-6 py-16 text-center text-gray-500">No expiring products</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full report-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Product</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">SKU</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Stock</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Expires</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Days Left</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {expiringProducts.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{p.name}</td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-500">{p.sku}</td>
                    <td className="px-4 py-3 text-sm text-center text-gray-700">{p.quantity_in_stock}</td>
                    <td className="px-4 py-3 text-sm text-center text-gray-700">{formatDate(p.expiry_date)}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-orange-600">{p.days_to_expiry}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Complete Inventory Register */}
      <div className="rounded-xl bg-white border border-[#EEF2F7] shadow-[0_2px_8px_rgba(15,23,42,0.05)] overflow-hidden">
        <div className="px-6 py-4 border-b border-[#EEF2F7]">
          <h2 className="text-lg font-semibold text-[#1F2937]">Complete Inventory Register</h2>
          <p className="text-sm text-gray-500 mt-1">{inventoryRegister.length} products in inventory</p>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          </div>
        ) : inventoryRegister.length === 0 ? (
          <div className="px-6 py-16 text-center text-gray-500">No products in inventory</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full report-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Product</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">SKU</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Supplier</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Purchase</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Selling</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Stock</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Expiry</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {inventoryRegister.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{p.name}</td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-500">{p.sku}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 capitalize">{p.category}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{p.supplier || '—'}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-600">{currency(p.purchase_price)}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-green-700">{currency(p.selling_price)}</td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className={p.quantity_in_stock <= p.min_stock_level ? 'font-bold text-red-700' : 'text-gray-700'}>
                        {p.quantity_in_stock}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {p.expiry_date ? formatDate(p.expiry_date) : <span className="text-gray-400">N/A</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

return (
    <ReportLayout
      title="Inventory Report"
      description="Monitor stock and purchasing"
      dateRange={dateRange}
      onDateRangeChange={setDateRange}
      onExportCsv={exportCsv}
      onPrint={() => window.print()}
      printLink="/reports/inventory/print"
      kpis={topKpis}
      leftSlot={leftSlot}
      rightSlot={rightSlot}
      tablesSlot={tablesSlot}
    />
  );
};

export default ReportsInventory;